import React__default, { useRef, useState, useMemo, useEffect } from 'react';
import { formatMoney } from '../../utils/stringUtils.js';
import { getEntityImage } from '../../utils/assetUtils.js';
import '../../analytics/events.js';
import '../../node_modules/tslib/tslib.es6.js';
import 'uuid';
import 'lodash/includes';
import { getAppConsistentParams } from '../../utils/appUtils.js';
import '../../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import '../../utils/broadcast.js';
import { MOBILE_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING, TABLET_SCREEN_SIZE, TABLET_HORIZONTAL_PADDING } from '../../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '@splitsoftware/splitio-react';
import '@tanstack/react-query';
import '../../Auth/index.js';
import '../../types/Campaign.js';
import { getProductUrl } from '../../utils/productUtils.js';
import '../../api/graphQl/brand.js';
import '../../api/graphQl/product.js';
import '../../api/cache/appConfigQueryOptions.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import '../../utils/logger.js';
import { VariantPickerWidget } from '@brandclub/bc-widgets';

// Helper to convert font weight string to CSS value
const getFontWeight = (weight) => {
    const weightMap = {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    };
    return weightMap[weight] || 500;
};
// Visually hidden text for screen readers
const VisuallyHidden = ({ children }) => (React__default.createElement("span", { style: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 } }, children));
/**
 * Get the display name for a variant from its variationNameValues
 * Prioritizes sizeName, then colorName, then first available value
 * Falls back to full title (CSS handles truncation)
 */
const getVariantDisplayName = (variant) => {
    const values = variant.variationNameValues;
    if (values && Object.keys(values).length > 0) {
        // Priority order for display name
        const priorityKeys = ['sizeName', 'colorName', 'styleName', 'patternName'];
        for (const key of priorityKeys) {
            if (values[key])
                return values[key];
        }
        // Fall back to first available value
        const firstValue = Object.values(values)[0];
        if (firstValue)
            return firstValue;
    }
    // No variation values - use full title (CSS will truncate)
    return variant.title || 'Variant';
};
/**
 * Get the first image URL for a variant
 */
const getVariantImage = (variant) => {
    if (variant.imageUrls && variant.imageUrls.length > 0) {
        return variant.imageUrls[0];
    }
    // Fall back to entity image based on SKU
    return getEntityImage(variant.stacklineSku, 'product');
};
/**
 * Sort variants alphabetically by their display name for consistent ordering
 * Uses stacklineSku as a secondary sort key to ensure stable ordering when names are equal
 */
const sortVariants = (variants) => {
    return [...variants].sort((a, b) => {
        const nameA = getVariantDisplayName(a).toLowerCase();
        const nameB = getVariantDisplayName(b).toLowerCase();
        const nameCompare = nameA.localeCompare(nameB);
        // Use stacklineSku as tiebreaker for consistent ordering
        if (nameCompare !== 0)
            return nameCompare;
        return a.stacklineSku.localeCompare(b.stacklineSku);
    });
};
/**
 * Rectangle card variant - used when all variants fit on one line
 * Shows image, price, and variant name
 */
const RectangleVariantCard = ({ variant, isSelected, onClick, state }) => {
    const [isHovered, setIsHovered] = useState(false);
    const displayName = getVariantDisplayName(variant);
    const fullTitle = variant.title || displayName;
    const imageUrl = getVariantImage(variant);
    const price = variant.retailPrice;
    const priceText = price !== undefined ? formatMoney(price) : '';
    return (React__default.createElement("button", { type: "button", onClick: onClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), title: fullTitle, "aria-pressed": isSelected, "aria-label": `${fullTitle}${priceText ? `, ${priceText}` : ''}${isSelected ? ', currently selected' : ''}`, style: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: state.borderRadius,
            backgroundColor: isHovered && !isSelected ? state.backgroundColorHover : state.backgroundColor,
            width: state.cardWidth,
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'all 0.2s',
            overflow: 'visible',
            border: `1px solid ${isSelected ? state.borderColorSelected : state.borderColor}`,
            padding: 0,
        } },
        isSelected && (React__default.createElement("svg", { width: state.checkmarkSize, height: state.checkmarkSize, viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { position: 'absolute', top: -8, right: -8, zIndex: 1 }, "aria-hidden": "true" },
            React__default.createElement("circle", { cx: "8", cy: "8", r: "8", fill: state.checkmarkBgColor }),
            React__default.createElement("path", { d: "M4.5 8L7 10.5L11.5 6", stroke: state.checkmarkIconColor, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))),
        isSelected && React__default.createElement(VisuallyHidden, null, "Selected:"),
        React__default.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, paddingBottom: 8 } },
            React__default.createElement("div", { style: { width: state.cardImageSize, height: state.cardImageSize, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
                React__default.createElement("img", { src: imageUrl, alt: "", "aria-hidden": "true", style: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 } }))),
        React__default.createElement("div", { style: { width: '100%', height: 1, backgroundColor: isSelected ? state.dividerColorSelected : state.dividerColor } }),
        React__default.createElement("div", { style: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 8 } },
            price !== undefined && (React__default.createElement("span", { style: {
                    fontSize: state.priceFontSize,
                    color: state.priceColor,
                    lineHeight: '20px',
                    fontWeight: getFontWeight(isSelected ? state.priceFontWeightSelected : state.priceFontWeightNormal),
                }, "aria-hidden": "true" }, priceText)),
            React__default.createElement("span", { style: {
                    fontSize: state.nameFontSize,
                    color: state.nameColor,
                    lineHeight: '20px',
                    textAlign: 'left',
                    width: '100%',
                    fontWeight: getFontWeight(isSelected ? state.nameFontWeightSelected : state.nameFontWeightNormal),
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }, "aria-hidden": "true" }, displayName))));
};
/**
 * Compact pill variant - used when variants need to wrap to multiple lines
 * Shows small image and variant name only (no price)
 */
const CompactVariantPill = ({ variant, isSelected, onClick, state }) => {
    const [isHovered, setIsHovered] = useState(false);
    const displayName = getVariantDisplayName(variant);
    const fullTitle = variant.title || displayName;
    const imageUrl = getVariantImage(variant);
    return (React__default.createElement("button", { type: "button", onClick: onClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), title: fullTitle, "aria-pressed": isSelected, "aria-label": `${fullTitle}${isSelected ? ', currently selected' : ''}`, style: {
            display: 'flex',
            alignItems: 'center',
            gap: state.pillGap,
            borderRadius: state.borderRadius,
            backgroundColor: isHovered && !isSelected ? state.backgroundColorHover : state.backgroundColor,
            padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
            paddingLeft: state.pillPaddingY,
            cursor: 'pointer',
            transition: 'all 0.2s',
            maxWidth: 200,
            border: `1px solid ${isSelected ? state.borderColorSelected : state.borderColor}`,
        } },
        isSelected && React__default.createElement(VisuallyHidden, null, "Selected:"),
        React__default.createElement("div", { style: { width: state.pillImageSize, height: state.pillImageSize, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
            React__default.createElement("img", { src: imageUrl, alt: "", "aria-hidden": "true", style: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 } })),
        React__default.createElement("span", { style: {
                fontSize: state.nameFontSizeCompact,
                color: state.nameColor,
                fontWeight: getFontWeight(isSelected ? state.nameFontWeightSelected : state.nameFontWeightNormal),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }, "aria-hidden": "true" }, displayName)));
};
/**
 * ProductVariantPicker - Adaptive component that switches between rectangle cards
 * and compact pills based on available container width.
 * Wrapped with VariantPickerWidget for theme customization support.
 */
const ProductVariantPicker = ({ currentStacklineSku, variants, onVariantSelect, navigate, }) => {
    const containerRef = useRef(null);
    const [useCompactLayout, setUseCompactLayout] = useState(false);
    const [showAllVariants, setShowAllVariants] = useState(false);
    // Sort variants for consistent ordering across pages
    const sortedVariants = useMemo(() => sortVariants(variants), [variants]);
    // Determine if we should show the "View more" button (more than 10 variants)
    const shouldShowToggle = sortedVariants.length > 10;
    const variantsToShow = shouldShowToggle && !showAllVariants ? sortedVariants.slice(0, 10) : sortedVariants;
    // Don't render if no variants or only one variant (nothing to switch to)
    if (!variants || variants.length <= 1) {
        return null;
    }
    const handleVariantClick = (variant) => {
        const { stacklineSku } = variant;
        // Don't navigate if clicking the already selected variant
        if (stacklineSku === currentStacklineSku)
            return;
        // Use callback if provided (Concierge widget pattern)
        if (onVariantSelect) {
            onVariantSelect(stacklineSku);
            return;
        }
        // Use direct navigation if navigate is provided (PDP pattern)
        if (navigate) {
            const urlSearchParams = getAppConsistentParams(window.location.search);
            const targetUrl = getProductUrl({
                title: variant.title,
                stacklineSku: variant.stacklineSku,
                urlSearchParams,
            });
            navigate({
                pathname: targetUrl.split('?')[0],
                search: targetUrl.split('?')[1],
            });
        }
    };
    return (React__default.createElement(VariantPickerWidget, { widgetId: "product-variant-picker", displayName: "Variant Picker" }, (state) => (React__default.createElement(ProductVariantPickerInner, { containerRef: containerRef, useCompactLayout: useCompactLayout, setUseCompactLayout: setUseCompactLayout, showAllVariants: showAllVariants, setShowAllVariants: setShowAllVariants, sortedVariants: sortedVariants, shouldShowToggle: shouldShowToggle, variantsToShow: variantsToShow, currentStacklineSku: currentStacklineSku, handleVariantClick: handleVariantClick, state: state }))));
};
// Inner component to handle the layout logic with access to widget state
const ProductVariantPickerInner = ({ containerRef, useCompactLayout, setUseCompactLayout, showAllVariants, setShowAllVariants, sortedVariants, shouldShowToggle, variantsToShow, currentStacklineSku, handleVariantClick, state, }) => {
    // Calculate responsive padding for mobile/tablet
    const [paddingLeft, setPaddingLeft] = useState(0);
    // Calculate if we need compact layout based on container width
    useEffect(() => {
        if (!containerRef.current)
            return;
        const checkLayout = () => {
            var _a;
            const containerWidth = ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
            const totalCardsWidth = sortedVariants.length * state.cardWidth + (sortedVariants.length - 1) * state.cardGap;
            // Use compact layout if cards don't fit in one line
            setUseCompactLayout(totalCardsWidth > containerWidth);
        };
        // Initial check
        checkLayout();
        // Set up ResizeObserver for responsive behavior
        const resizeObserver = new ResizeObserver(checkLayout);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [sortedVariants.length, state.cardWidth, state.cardGap, containerRef, setUseCompactLayout]);
    useEffect(() => {
        const updatePadding = () => {
            const width = window.innerWidth;
            if (width <= MOBILE_SCREEN_SIZE) {
                setPaddingLeft(MOBILE_HORIZONTAL_PADDING);
            }
            else if (width <= TABLET_SCREEN_SIZE) {
                setPaddingLeft(TABLET_HORIZONTAL_PADDING);
            }
            else {
                setPaddingLeft(0);
            }
        };
        updatePadding();
        window.addEventListener('resize', updatePadding);
        return () => window.removeEventListener('resize', updatePadding);
    }, []);
    return (React__default.createElement("div", { ref: containerRef, id: "product-variant-picker", style: {
            width: '100%',
            marginBottom: 20,
            paddingLeft: paddingLeft > 0 ? paddingLeft : undefined,
        } },
        React__default.createElement("div", { role: "group", "aria-label": `Product variants, ${sortedVariants.length} options available`, style: {
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'stretch',
                paddingRight: 8,
                gap: useCompactLayout ? state.pillGap : state.cardGap,
            } }, variantsToShow.map((variant) => {
            const isSelected = variant.stacklineSku === currentStacklineSku;
            if (useCompactLayout) {
                return (React__default.createElement(CompactVariantPill, { key: variant.stacklineSku, variant: variant, isSelected: isSelected, onClick: () => handleVariantClick(variant), state: state }));
            }
            return (React__default.createElement(RectangleVariantCard, { key: variant.stacklineSku, variant: variant, isSelected: isSelected, onClick: () => handleVariantClick(variant), state: state }));
        })),
        shouldShowToggle && (React__default.createElement("button", { type: "button", onClick: () => setShowAllVariants(!showAllVariants), style: {
                marginTop: 12,
                fontSize: 14,
                fontWeight: 500,
                color: state.nameColor,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                textDecoration: 'none',
            }, "aria-expanded": showAllVariants }, showAllVariants ? 'View less' : 'View more'))));
};

export { ProductVariantPicker };
//# sourceMappingURL=ProductVariantPicker.js.map
